import { FC, useState } from "react";

import { Stack } from "@mui/material";

import { EmbedTab } from "./EmbedTab";
import { QrCodeTab } from "./QrCodeTab";

import { useTranslate } from "@/common/hooks/useTranslate";
import { CloseDialogButton } from "@/components/ShareFormModal/CloseDialogButton";
import { ShareFormModal } from "@/components/ShareFormModal/ShareFormModal";
import { ShareFormTabLayout } from "@/components/ShareFormModal/ShareFormTabLayout";
import { ShareFormTabTitle } from "@/components/ShareFormModal/ShareFormTabTitle";
import { ShareThis, ShareThisButton } from "@/components/ShareThis/ShareThis";
import { TextFieldWithCopy } from "@/components/TextFieldWithCopy/TextFieldWithCopy";
import { DashboardDivider } from "@/dashboard-design-system/Divider";
import { DashboardSelectBar } from "@/dashboard-design-system/SelectBar";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

type ShareOrgPageModalTab = "home" | "qrCode" | "embed";

type ShareOrgPageModalProps = {
  open: boolean;
  onClose: () => void;
  orgPageUrl: string;
  mode?: "default" | "orgPageReady";
};

export const ShareOrgPageModal: FC<ShareOrgPageModalProps> = ({ open, onClose, orgPageUrl, mode = "default" }) => {
  const { t } = useTranslate();
  const [activeTab, setActiveTab] = useState<ShareOrgPageModalTab>("home");

  const handleClose = () => {
    setActiveTab("home");
    onClose();
  };

  if (activeTab === "qrCode") {
    return (
      <ShareFormModal open={open} onClose={handleClose}>
        <QrCodeTab orgPageUrl={orgPageUrl} onClose={handleClose} onBackClick={() => setActiveTab("home")} />
      </ShareFormModal>
    );
  }

  if (activeTab === "embed") {
    return (
      <ShareFormModal open={open} onClose={handleClose}>
        <EmbedTab orgPageUrl={orgPageUrl} onClose={handleClose} onBackClick={() => setActiveTab("home")} />
      </ShareFormModal>
    );
  }

  return (
    <ShareFormModal open={open} onClose={handleClose}>
      <ShareFormTabLayout
        slots={{
          closeButton: <CloseDialogButton onClick={handleClose} />,
          header: (
            <Stack width="100%" alignItems="center" gap={2.5}>
              <ShareFormTabTitle>
                {mode === "orgPageReady"
                  ? t("organization", "shareModal.title.orgPageReady")
                  : t("organization", "shareModal.title")}
              </ShareFormTabTitle>
              <TextFieldWithCopy
                value={orgPageUrl}
                button
                size="medium"
                buttonCopyProps={{
                  size: "medium",
                  copyText: t("organization", "shareModal.copyLink"),
                }}
                data-test="share-org-page-modal-link"
              />
              <ShareThis
                buttonSize={40}
                spaceBetweenButton={2}
                noMargin
                buttons={[ShareThisButton.Facebook, ShareThisButton.Messenger, ShareThisButton.Email]}
                url={orgPageUrl}
              />
            </Stack>
          ),
          content: (
            <Stack gap={2}>
              <DashboardDivider>
                <DashboardTypography variant="body2" vibe="text-neutral-supershy">
                  {t("common", "or")}
                </DashboardTypography>
              </DashboardDivider>
              <DashboardSelectBar
                title={t("organization", "shareModal.qrCode.title")}
                description={t("organization", "shareModal.qrCode.description")}
                onClick={() => setActiveTab("qrCode")}
                data-test="share-org-page-modal-qr-code"
              />
              <DashboardSelectBar
                title={t("organization", "shareModal.embed.title")}
                description={t("organization", "shareModal.embed.description")}
                onClick={() => setActiveTab("embed")}
                data-test="share-org-page-modal-embed"
              />
            </Stack>
          ),
        }}
      />
    </ShareFormModal>
  );
};
